import React, { useEffect, useRef, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Typography,
} from "@mui/material"
import { CloudUpload as CloudUploadIcon } from "@mui/icons-material"
import { useSnackbar } from "notistack"
import { uploadTranscriptBulkImportZip, getTranscriptBulkImportStatus } from "services/aiAgent"
import { verifyResponse } from "utils/httpRequest"

const POLL_INTERVAL_MS = 2000

const TranscriptBulkImportCard = ({ onImportComplete }) => {
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [jobId, setJobId] = useState(null)
  const [jobStatus, setJobStatus] = useState(null)
  const pollRef = useRef(null)
  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [])

  const handleFileInput = (e) => {
    const file = e.target.files && e.target.files[0]
    if (!file) return
    if (!file.name.toLowerCase().endsWith(".zip")) {
      enqueueSnackbar("Please select a .zip file", { variant: "error" })
      return
    }
    setSelectedFile(file)
    setJobId(null)
    setJobStatus(null)
  }

  const startPolling = (id) => {
    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = setInterval(async () => {
      try {
        const response = await getTranscriptBulkImportStatus(id)
        const status = response?.data ?? response
        setJobStatus(status)
        if (status.state === "completed" || status.state === "failed") {
          clearInterval(pollRef.current)
          pollRef.current = null
          if (status.state === "completed") {
            enqueueSnackbar("Transcript bulk import completed", { variant: "success" })
            onImportComplete?.()
          } else {
            enqueueSnackbar(`Bulk import failed: ${status.failedReason || "unknown error"}`, { variant: "error" })
          }
        }
      } catch (error) {
        console.error("Error polling bulk import status:", error)
      }
    }, POLL_INTERVAL_MS)
  }

  const handleUpload = async () => {
    if (!selectedFile) {
      enqueueSnackbar("Select a .zip file first", { variant: "warning" })
      return
    }

    setUploading(true)
    setUploadProgress(0)
    try {
      const response = await uploadTranscriptBulkImportZip(selectedFile, setUploadProgress)
      verifyResponse(response)
      if (response?.rtnCode && response.rtnCode !== 200) {
        throw new Error(response.message || `Upload failed (code ${response.rtnCode})`)
      }
      const payload = response?.data ?? response
      const newJobId = payload?.jobId
      const fileCount = payload?.fileCount
      if (!newJobId) throw new Error(response?.message || "No job id returned in response")
      setJobId(newJobId)
      enqueueSnackbar(`Queued ${fileCount ?? "?"} transcript(s) for import`, { variant: "success" })
      startPolling(newJobId)
    } catch (error) {
      console.error("Error uploading transcript zip:", error)
      const message = error.response?.data?.message || error.message || "Error uploading zip"
      enqueueSnackbar(message, { variant: "error" })
    } finally {
      setUploading(false)
    }
  }

  const progress = jobStatus?.progress
  const isRunning = jobId && jobStatus?.state !== "completed" && jobStatus?.state !== "failed"
  const percent = progress?.total ? Math.round((progress.processed / progress.total) * 100) : 0

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
          Bulk Import Transcripts (.zip)
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Upload a single .zip file containing .txt transcripts (flat, no subfolders). Each file is
          matched by name to its lesson's media file when possible — matched transcripts are attached
          to that lesson, and every transcript (matched or not) is added to this shared knowledge base
          for all coaches. Nothing is uploaded to S3 for this import; the text is stored directly in the
          database. Already-imported files (by content) are skipped automatically on re-upload.
        </Typography>

        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUploadIcon />}
            disabled={uploading || isRunning}
          >
            Choose .zip file
            <input type="file" accept=".zip" hidden onChange={handleFileInput} />
          </Button>
          {selectedFile && (
            <Chip label={selectedFile.name} onDelete={() => setSelectedFile(null)} disabled={uploading || isRunning} />
          )}
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!selectedFile || uploading || isRunning}
          >
            {uploading ? "Uploading..." : "Upload & Import"}
          </Button>
        </Box>

        {uploading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
            <Typography variant="caption" color="textSecondary">Uploading zip: {uploadProgress}%</Typography>
          </Box>
        )}

        {jobId && progress && (
          <Box sx={{ mt: 2 }}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2">
                {isRunning ? "Importing..." : jobStatus.state === "completed" ? "Import complete" : "Import failed"}{" "}
                ({progress.processed}/{progress.total})
              </Typography>
              <Typography variant="body2" color="primary">{percent}%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={percent} color={jobStatus.state === "failed" ? "error" : "primary"} />
            <Box display="flex" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
              <Chip size="small" color="success" label={`Matched to lesson: ${progress.matched}`} />
              <Chip size="small" label={`Unmatched: ${progress.unmatched}`} />
              <Chip size="small" label={`Already imported: ${progress.kbSkippedAlreadyImported}`} />
              {progress.failed > 0 && <Chip size="small" color="error" label={`Failed: ${progress.failed}`} />}
            </Box>
            {jobStatus.state === "completed" && jobStatus.result?.failures?.length > 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {jobStatus.result.failures.length} file(s) failed, e.g. "{jobStatus.result.failures[0].fileName}":{" "}
                {jobStatus.result.failures[0].error}
              </Alert>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default TranscriptBulkImportCard
